import * as React from 'react';
import {
  useContext, useState, useEffect, useRef,
} from 'react';
import moment from 'moment';
import { fr } from 'date-fns/locale/fr';
import { v4 as uuidv4 } from 'uuid';
import PerfectScrollbar from 'react-perfect-scrollbar';
import DatePicker, { registerLocale } from 'react-datepicker';
import useOutsideAlerter from '../../../hooks/UseOutsideAlerter';
import LocalizationContext from '../../../contexts/Localizations';
import SvgImage from '../SvgImage';
import './styles.scss';

registerLocale('fr', fr);

interface JumpListOption {
  displayText: string;
  value: string;
  selectedValue?: boolean;
  visible?: boolean;
  name?: string;
}

interface JumpListProps {
  items: JumpListOption[];
  selectedValue?: string;
  action: Function;
  icon?: string;
  theme?: string;
  useScroller?: boolean;
  useSearch?: boolean;
  name?: string;
  disableFirst?: boolean;
  withDatePicker?: boolean;
  defaultDatePickerValue?: Date;
  onDateChange?: Function;
  includeDates?: Date[];
  datePickerLocale?: string;
}

const JumpList = ({
  items,
  action,
  selectedValue,
  icon = 'icon-arrow-down',
  theme = 'default',
  useScroller = false,
  useSearch = false,
  name,
  disableFirst = false,
  withDatePicker = false,
  defaultDatePickerValue,
  onDateChange,
  includeDates,
  datePickerLocale,
}: JumpListProps) => {
  const wrapperRef = useRef<React.Ref<HTMLDivElement>>(null);
  const [value, setValue] = useState<JumpListOption>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<JumpListOption[]>();
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const { translations } = useContext(LocalizationContext);

  /**
   * Closes the open view
   */
  const close = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsOpen(false);
      if (useSearch) {
        setSearchValue(null);
      }
    }, useSearch ? 0 : 300);
  };

  /**
   * This will tigger the close method when the user
   * interacts outside wrapperRef
   */
  useOutsideAlerter(wrapperRef, () => {
    close();
  });

  /**
   * Toggle open/close
   * @param event
   * @returns
   */
  const toggle = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }

    if (disableFirst) {
      return null;
    }

    if (isOpen) {
      return close();
    }

    return setIsOpen(!isOpen);
  };

  /**
   * This remaps the options base of the items and selectedValue
   * props.
   * @returns
   */
  const mapSelection = () => items.map((item, index) => ({
    ...item,
    name,
    selectedValue: item.value === selectedValue,
    visible: !(withDatePicker && defaultDatePickerValue && index > 4),
  }));

  /**
   * This handels the change event on the drop down.
   * @param event
   * @param optionIndex
   */
  const handleChange = (event: React.MouseEvent, optionIndex: number) => {
    event.preventDefault();
    setOptions(mapSelection());
    setValue(options ? options[optionIndex] : undefined);
    close();

    if (action && typeof action === 'function') {
      action(options ? options[optionIndex] : undefined);
    }
  };

  /**
   * Component has mounted hook
   */
  useEffect(() => {
    if (items.length) {
      const optionsSelection = mapSelection();
      setOptions(optionsSelection);
    }
  }, []);

  /**
   * Observe the options state to determine the selected value.
   * If a new value is selected with will trigger and setValue in state.
   */
  useEffect(() => {
    if (options) {
      const selected: JumpListOption = options.filter(
        (item: JumpListOption) => item.value === selectedValue,
      )[0];

      setValue(selected || options[0]);
    }
  }, [options, selectedValue]);

  /**
   * Observe the search value term to filter down the visible
   * option in the list. This is only applicable if the user
   * has turned on useSearch in props.
   */
  useEffect(() => {
    if (options) {
      if (searchValue === null || searchValue === '') {
        setOptions(mapSelection());
      } else {
        const filteredOptions: JumpListOption[] = options?.map((option) => ({
          ...option,
          visible: option.displayText.toLowerCase().includes(searchValue.toLowerCase()),
        }));

        setOptions(filteredOptions);
      }
    }
  }, [searchValue, selectedValue, items]);

  /**
   * Creates a list option
   * @param option
   * @param index
   * @returns
   */
  const createOption = (option: JumpListOption, index: number) => (
    <li key={uuidv4()} className={`jump-list__item ${option.selectedValue && 'jump-list__item--selected'}`}>
      <a href="#" role="button" onClick={(event: React.MouseEvent) => handleChange(event, index)}>
        {option.displayText}
      </a>
    </li>
  );

  /**
   * Creates the search JSX controls. This is only applicable
   * if the user has turned on useSearch in props.
   * @returns
   */
  const createSearch = () => (
    <div className="jump-list__search">
      <input
        type="text"
        placeholder={translations.calendar.searchLabel}
        value={searchValue || ''}
        onChange={(event: React.ChangeEvent) => (
          setSearchValue((event.currentTarget as HTMLInputElement).value)
        )}
      />
      {Boolean(!options?.filter((option) => option.visible).length) && (
        <div className="jump-list__no-results">
          {`No selection found for ${searchValue}.`}
          {/* Needs Laguage label */}
        </div>
      )}
    </div>
  );

  /**
   * Creates the option list.
   * @returns
   */
  const createOptions = () => {
    if (useScroller) {
      return (
        <div className={`jump-list__scroll-content ${withDatePicker ? 'with-date-picker' : ''}`}>
          {useSearch && createSearch()}
          <PerfectScrollbar>
            <>
              {options && (
                <ul className="jump-list__options">
                  {options?.map((option, key) => option.visible && createOption(option, key))}
                </ul>
              )}

              {withDatePicker && defaultDatePickerValue && (
                <DatePicker
                  selected={defaultDatePickerValue}
                  onChange={(date: Date, event: React.MouseEvent) => {
                    if (onDateChange) {
                      const dateIndex = options?.findIndex((option) => {
                        const optionMoment = moment(option.value).format('DD-MM-YYYY');
                        const selectedMoment = moment(date).format('DD-MM-YYYY');

                        return optionMoment === selectedMoment;
                      });

                      if (dateIndex !== undefined && dateIndex >= 0) {
                        onDateChange(date);

                        handleChange(event, dateIndex);
                      }
                    }
                  }}
                  includeDates={includeDates}
                  inline
                  locale={datePickerLocale}
                />
              )}
            </>
          </PerfectScrollbar>
        </div>
      );
    }

    return (
      <div className="jump-list__content">
        {useSearch && createSearch()}
        {options && (
          <ul className="jump-list__options">
            {options?.map((option, key) => option.visible && createOption(option, key))}
          </ul>
        )}

        {withDatePicker && defaultDatePickerValue && (
          <DatePicker
            selected={defaultDatePickerValue}
            onChange={onDateChange}
            includeDates={includeDates}
            inline
          />
        )}
      </div>
    );
  };

  return (
    <div
      // @ts-ignore
      ref={wrapperRef}
      className={
        `jump-list ${isOpen && 'jump-list--open'}
        ${isClosing && 'jump-list--closing'}
        jump-list--${theme}`
      }
    >
      <div className={`jump-list__value ${disableFirst && 'disabled'}`}>
        {value && (
          <div>
            <a href="#" onClick={(event: React.MouseEvent) => toggle(event)}>
              {value.displayText}
            </a>
          </div>
        )}
      </div>
      <div className={`jump-list__icon ${disableFirst && 'hide-icon'}`}>
        <SvgImage title="Filter" reference={`#${icon}`} />
      </div>
      {createOptions()}
    </div>
  );
};

export default JumpList;
